import { useState } from 'react';
import { message } from 'antd';
import dayjs from 'dayjs';
import { orderService } from '@/services/order.service';
import { exportReport, formatNumberCell, formatDateCell, type ExportColumn } from '@/utils/exportReport';
import { getOrderStateText, OrderStatusGroup } from '@tcr-erp/shared';
import {
    resolveOrderChannelLabel,
    ORDER_STATUS_GROUP_LABEL,
} from '@/pages/Orders/parts/constants';
import type { TenantOrder } from '@/mocks/orders';
import type { OrderListQuery } from '@/services/queryKeys';

export type OrderExportFilter = Omit<OrderListQuery, 'page' | 'page_size'>;

/**
 * TCR-1163: cột "Trạng thái" của file Excel lấy nhãn NHÓM TAB, đúng thứ người dùng
 * đang nhìn trên màn hình trước khi bấm xuất.
 *
 * Trước đây file này giữ một bảng nhãn riêng dịch theo `state`, là bảng nhãn thứ BA
 * trong hệ thống (sau bảng chuẩn ở shared và bảng ở màn danh sách). Ba bảng lệch nhau
 * chính là phần tester mô tả thành "xuất excel bị lệch trạng thái": lọc tab "Thành
 * công" rồi xuất ra lại thấy cột ghi "Đang giao", vì tab đọc nhóm còn cột đọc `state`.
 * Nay bảng riêng bị xoá, chỉ còn nhãn nhóm tab và nhãn chuẩn của shared làm dự phòng.
 */
export function resolveExportStatusLabel(order: {
    state?: string;
    status_group?: string | null;
}): string {
    const group = order.status_group;
    if (group && group !== OrderStatusGroup.ALL) {
        const label = ORDER_STATUS_GROUP_LABEL[group as OrderStatusGroup];
        if (label) return label;
    }
    return getOrderStateText(order.state ?? '');
}

const PAYMENT_TEXT: Record<string, string> = {
    cash: 'Tiền mặt',
    transfer: 'Chuyển khoản',
    card: 'Thẻ',
    mix: 'Hỗn hợp',
};

export function formatStateHistory(stateHistory?: TenantOrder['state_history']): string {
    if (!stateHistory || stateHistory.length === 0) return '';
    return stateHistory
        .map((h) => {
            // Lịch sử vốn là chuỗi TRẠNG THÁI ĐƠN nên vẫn dịch theo state — nhưng dùng
            // bảng nhãn chuẩn của shared, không dựng bảng riêng nữa.
            const label = getOrderStateText(h.state);
            const at = h.changed_at ? dayjs(h.changed_at).format('HH:mm DD/MM/YYYY') : '';
            return `${label}${at ? ` (${at})` : ''}`;
        })
        .join(' → ');
}

/**
 * Bộ cột của file Excel đơn hàng ngoài sàn. Khai báo MỘT chỗ duy nhất để bản xuất danh sách
 * và bản xuất 1 đơn từ pop-up (TCR-2480) ra cùng một file: hai nơi tự khai báo cột thì lần
 * sửa kế tiếp sẽ lệch nhau ngay, đúng kiểu bug "cùng field, nhiều nơi tính lại".
 */
const ORDER_EXPORT_COLUMNS: ExportColumn[] = [
    { key: 'stt', header: 'STT' },
    { key: 'order_number', header: 'Mã đơn' },
    { key: 'state', header: 'Trạng thái' },
    { key: 'order_date', header: 'Ngày tạo đơn', format: formatDateCell },
    { key: 'product_skus', header: 'Mã sản phẩm' },
    { key: 'product_names', header: 'Tên sản phẩm' },
    // TCR-2625 — file 1 dòng/đơn nên ghi tóm tắt quà của cả đơn; trống = không có quà.
    { key: 'gift_summary', header: 'Quà tặng' },
    { key: 'total_qty', header: 'Số lượng sản phẩm', format: formatNumberCell },
    { key: 'total_amount', header: 'Tổng giá trị', format: formatNumberCell },
    { key: 'discount', header: 'Chiết khấu', format: formatNumberCell },
    { key: 'prepaid_amount', header: 'Khách trả trước', format: formatNumberCell },
    { key: 'shipping_cost', header: 'Phí vận chuyển', format: formatNumberCell },
    { key: 'customer_name', header: 'Khách hàng' },
    { key: 'customer_phone', header: 'SĐT' },
    { key: 'tracking_number', header: 'Mã vận đơn' },
    { key: 'carrier', header: 'Đơn vị vận chuyển' },
    { key: 'address', header: 'Địa chỉ' },
    { key: 'city_name', header: 'Tỉnh/Thành phố' },
    { key: 'customer_note', header: 'Ghi chú của khách' },
    { key: 'shipping_note', header: 'Ghi chú vận chuyển' },
    { key: 'cashier_name', header: 'Nhân viên' },
    { key: 'cod_amount', header: 'Tiền thu hộ (COD)', format: formatNumberCell },
    { key: 'shipping_fee_actual', header: 'Phí vận chuyển thực tế', format: formatNumberCell },
    { key: 'state_handler_name', header: 'Xử lý đơn hàng' },
    { key: 'state_handler_changed_at', header: 'Thời gian xử lý', format: formatDateCell },
    { key: 'warehouse_names', header: 'Kho hàng' },
    { key: 'fee_payer', header: 'Bên trả phí vận chuyển' },
    { key: 'order_revenue', header: 'Doanh thu đơn hàng', format: formatNumberCell },
    { key: 'unit_names', header: 'Đơn vị tính' },
    { key: 'delivery_date', header: 'Ngày giao hàng', format: formatDateCell },
    { key: 'reconciliation_date', header: 'Ngày đối soát' },
    { key: 'buyer_invoice_name', header: 'Tên xuất hoá đơn' },
    { key: 'buyer_invoice_tax_id', header: 'MST/CCCD xuất hoá đơn' },
    { key: 'buyer_invoice_address', header: 'Địa chỉ xuất hoá đơn' },
    { key: 'payment_method', header: 'Hình thức thanh toán' },
    { key: 'channel', header: 'Kênh' },
    { key: 'state_history', header: 'Lịch sử trạng thái' },
];

/**
 * TCR-2625 — ô "Quà tặng": "9 SP: Móc khoá x9; Túi vải x1" (tổng SL quà rồi từng SP quà và SL).
 * Đơn không có SP thuộc danh mục Gift → chuỗi rỗng.
 */
export function formatGiftSummaryCell(summary: TenantOrder['gift_summary']): string {
    if (!summary || summary.items.length === 0) return '';
    const list = summary.items.map((item) => `${item.name || item.sku || 'Sản phẩm quà tặng'} x${item.quantity}`).join('; ');
    return `${summary.total_quantity} SP: ${list}`;
}

/** Một đơn → một dòng Excel. Tách khỏi hook để bản xuất lẻ dùng lại đúng phép biến đổi này. */
function orderToExportRow(order: TenantOrder, idx: number): Record<string, unknown> {
    const totalQty = order.items?.reduce((sum, it) => sum + (it.qty ?? 0), 0) ?? 0;
    const productNames = order.items?.map(it => it.product_name).filter(Boolean).join('; ') ?? '';
    const productSkus = order.items?.map(it => it.sku).filter(Boolean).join('; ') ?? '';
    const warehouseNames = order.items?.map(it => it.warehouse_name).filter(Boolean).join('; ') ?? '';
    const unitNames = order.items?.map(it => it.unit_name).filter(Boolean).join('; ') ?? '';
    return {
        stt: idx + 1,
        order_number: order.order_number,
        state: resolveExportStatusLabel(order),
        order_date: order.order_date ?? order.created_at,
        product_skus: productSkus,
        product_names: productNames,
        gift_summary: formatGiftSummaryCell(order.gift_summary),
        total_qty: totalQty,
        total_amount: order.total_amount,
        discount: order.discount ?? 0,
        prepaid_amount: Number(order.shipping?.prepaid_amount ?? 0),
        shipping_cost: Number(order.shipping?.shipping_cost ?? 0),
        customer_name: order.customer?.name ?? '',
        customer_phone: order.customer?.phone ?? '',
        tracking_number: order.shipping?.tracking_number ?? '',
        carrier: order.shipping?.carrier ?? '',
        address: order.shipping?.address?.address ?? '',
        city_name: order.shipping_city_name ?? '',
        customer_note: order.note ?? '',
        // TCR-1221: ghi chú cho ĐVVC — field top-level riêng, KHÁC
        // `note` (ghi chú của khách) và không lồng trong `shipping`.
        shipping_note: order.shipping_note ?? '',
        cashier_name: order.cashier?.name ?? '',
        cod_amount: Number(order.shipping?.cod_amount ?? 0),
        shipping_fee_actual: order.shipping_fee_actual ?? 0,
        state_handler_name: order.state_handler_name ?? '',
        state_handler_changed_at: order.state_handler_changed_at ?? '',
        warehouse_names: warehouseNames,
        fee_payer: order.shipping?.fee_payer === 'buyer' ? 'Người mua' : order.shipping?.fee_payer === 'seller' ? 'Người bán' : '',
        // TCR-2480: đơn lấy từ trang chi tiết KHÔNG có `order_revenue` cấp đơn (chỉ aggregate danh
        // sách mới set, xem `mapRawOrder`) — số thật nằm ở `summary.order_revenue`. Đọc dự phòng để
        // file xuất lẻ không ghi 0; dòng của danh sách luôn có field cấp đơn nên không đổi.
        order_revenue: order.order_revenue ?? order.summary?.order_revenue ?? 0,
        unit_names: unitNames,
        delivery_date: order.delivery_date ?? '',
        // Ngày đối soát — chưa có nguồn dữ liệu, luôn để trống (không bịa số liệu).
        reconciliation_date: '',
        buyer_invoice_name: order.buyer_invoice_info?.name ?? '',
        buyer_invoice_tax_id: order.buyer_invoice_info?.tax_id ?? '',
        buyer_invoice_address: order.buyer_invoice_info?.address ?? '',
        payment_method: order.payment_method ? (PAYMENT_TEXT[order.payment_method] ?? order.payment_method) : '',
        channel: resolveOrderChannelLabel(order),
        state_history: formatStateHistory(order.state_history),
    };
}

/**
 * TCR-2480: phần dựng dòng/cột + gọi `exportReport` tách hẳn khỏi hook. Trước đây toàn bộ
 * nằm trong `useExportOrders`, mà hook đó bám vào `orderService.exportList` (bộ lọc của danh
 * sách) nên pop-up chi tiết đơn không có đường nào xuất lẻ được đơn đang xem.
 *
 * `message.success/error` giữ y nguyên câu chữ cũ để người dùng không thấy hai kiểu thông báo
 * cho cùng một thao tác.
 *
 * @param filenameSuffix đuôi tên file — danh sách truyền ngày, xuất lẻ truyền mã đơn.
 */
export async function exportOrdersToExcel(
    items: TenantOrder[],
    filenameSuffix?: string,
): Promise<void> {
    const rows = items.map(orderToExportRow);
    try {
        await exportReport({
            filename: `don-hang-${filenameSuffix ?? new Date().toISOString().slice(0, 10)}`,
            format: 'excel',
            title: 'Danh sách đơn hàng',
            sheets: [
                {
                    name: 'Đơn hàng',
                    columns: ORDER_EXPORT_COLUMNS,
                    rows,
                },
            ],
        });

        void message.success(`Xuất thành công ${items.length} đơn hàng`);
    } catch {
        void message.error('Xuất Excel thất bại');
    }
}

/**
 * TCR-1627: phần dựng dữ liệu + gọi `exportReport` được tách khỏi nút bấm, vì nút rời
 * "Xuất Excel" nay là một MỤC trong dropdown "Excel" (`OrderExcelButton`). Nội dung file
 * xuất ra giữ nguyên từng cột — gộp nút là thay đổi chỗ bấm, không phải đổi dữ liệu.
 *
 * TCR-2480: thân hàm nay chỉ còn "lấy danh sách theo bộ lọc" rồi giao phần dựng file cho
 * `exportOrdersToExcel`, để bản xuất 1 đơn dùng chung đúng một bộ cột.
 */
export function useExportOrders(filter: OrderExportFilter) {
    const [loading, setLoading] = useState(false);

    const handleExport = async () => {
        setLoading(true);
        try {
            const { items } = await orderService.exportList(filter);
            await exportOrdersToExcel(items);
        } catch {
            // Chỉ còn nhánh `exportList` lỗi rơi vào đây — `exportOrdersToExcel` đã tự báo
            // lỗi riêng và không ném ra ngoài.
            void message.error('Xuất Excel thất bại');
        } finally {
            setLoading(false);
        }
    };

    return { exporting: loading, exportOrders: handleExport };
}

/**
 * TCR-2480: xuất Excel ĐÚNG đơn đang mở trong pop-up. Cố ý KHÔNG gọi lại
 * `orderService.exportList` — bộ lọc của danh sách không mô tả được "đơn này", và gọi
 * vòng qua BE chỉ để lấy lại đúng dữ liệu đã có trong tay là thêm một điểm có thể lệch
 * (đơn vừa sửa xong, bộ lọc vừa đổi).
 */
export function useExportSingleOrder(order?: TenantOrder | null) {
    const [loading, setLoading] = useState(false);

    const exportSingleOrder = async () => {
        if (!order) return;
        setLoading(true);
        try {
            await exportOrdersToExcel([order], order.order_number);
        } finally {
            setLoading(false);
        }
    };

    return { exportingSingle: loading, exportSingleOrder };
}
